import { Component, lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Fallback } from "./app/Fallback";
import { MapShell, RequireAdmin, RequireAuth, Shell } from "./app/Shell";
import { RequireVerwaltung } from "./app/RequireVerwaltung";
import { KommunenAkzent } from "./app/KommunenAkzent";
import { anmeldeUrsprung, aufAppHost } from "./lib/hosts";
import { LoginScreen } from "./screens/LoginScreen";
import { ResetScreen } from "./screens/ResetScreen";
import { ProjectOverview } from "./screens/dashboard/ProjectOverview";
import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";

/**
 * Dashboard und Auswertungen sind EINE Seite: "/" springt direkt auf das
 * aktive Projekt; die Projektuebersicht lebt unter /overview weiter.
 *
 * Hier stand bis zuletzt `if (!projectsQ.data) return null;` — und ausgerechnet
 * diese Abfrage ist die teuerste der App: /api/projects sind 703 KB gzip, davon
 * 99,7 % Vorschaubilder als base64. Gemessen (gedrosselt, Prod): bei 400, 800,
 * 1500 und 3000 ms war der Inhaltsbereich LEER — nur Kopfleiste und Fusszeile um
 * eine weisse Flaeche —, und erst nach rund 4,5 s sprang die Adresse ueberhaupt
 * auf /project/:id. "/" ist der Standardeinstieg (Logo, Dashboard-Knopf): der
 * meistgenutzte Weg war der einzige ganz ohne Skelett.
 *
 * Zwei Aenderungen, in dieser Reihenfolge zu lesen:
 *  1. Wer schon einmal in einem Projekt war, springt SOFORT dorthin — ohne auf
 *     die Liste zu warten. Der Store haelt lastProjectId ueber das Neuladen
 *     hinweg, die Vermutung ist also gut belegt und im Regelfall richtig.
 *  2. Nur wenn nichts gemerkt ist, wird gewartet — dann aber mit dem
 *     Drahtgitter statt mit einer leeren Flaeche: die Endstruktur ist ja
 *     bekannt, es wird ein Dashboard.
 *
 * Zeigt lastProjectId auf ein entzogenes Projekt, greift der Waechter in
 * Shell.tsx (useEntzogenesProjekt) und schickt still auf das erste
 * zugaengliche. Ohne ihn waere Punkt 1 eine Falle: ProjectStats zeigt fuer ein
 * fremdes Projekt „Projekt nicht gefunden" und hat keinen Ausgang.
 */
function HomeRedirect() {
  const auth = useStore((st) => st.auth);
  const authGeprueft = useStore((st) => st.authGeprueft);
  const last = useStore((st) => st.lastProjectId);
  const projectsQ = useQuery({ queryKey: ["projects"], queryFn: () => api.projects.list(), enabled: !!auth });
  if (!auth) {
    // #18: keine oeffentliche Landung mehr. Auf app.* wohnt auch keine
    // Anmeldeseite — direkt zum Anmelde-Host (Shell-Guards machen dasselbe).
    // Aber erst NACH dem Boot-Abgleich: localStorage ist auf app.* leer,
    // waehrend das Domain-Cookie die Sitzung noch traegt.
    if (aufAppHost()) {
      if (!authGeprueft) return <SeitenSkelett />;
      location.href = `${anmeldeUrsprung()}/login`;
      return null;
    }
    return <Navigate to="/login" replace />;
  }
  // Liste schon da (Zwischenspeicher oder zweiter Besuch): gesicherte Wahl.
  if (projectsQ.data) {
    const target = last && projectsQ.data.some((p) => p.id === last) ? last : projectsQ.data[0]?.id;
    if (!target) return <ProjectOverview />; // keinerlei Projekte → Uebersicht (Admin legt an)
    return <Navigate to={`/project/${target}`} replace />;
  }
  if (last) return <Navigate to={`/project/${last}`} replace />; // Vorgriff, s. oben
  return <SeitenSkelett />;
}
import { Btn, Icon, IconBtn } from "./components/ui";
// retryOnStaleShell wohnt in lib/chunk: das Diagrammmodul laedt sich ueber
// denselben Weg nach und braucht dieselbe Selbstheilung (s. dort).
import { chunkRiegelLoesen, retryOnStaleShell } from "./lib/chunk";

/**
 * Fehlergrenze um die Bildschirme.
 *
 * Ohne sie beendet ein geworfener Fehler beim Rendern den gesamten Baum und
 * hinterlaesst eine weisse Flaeche — kein Hinweis, kein Weg zurueck, nur eine
 * Seite, die nicht laedt. Das ist das schlechteste aller moeglichen Ergebnisse:
 * der Nutzer kann nicht einmal berichten, WAS nicht ging.
 */
class ScreenBoundary extends Component<{ children: ReactNode }, { err: Error | null }> {
  state: { err: Error | null } = { err: null };

  static getDerivedStateFromError(err: Error) {
    return { err };
  }

  componentDidCatch(err: Error) {
    // bleibt in der Browserkonsole stehen, damit ein Bericht etwas hergibt
    console.error("Bildschirm konnte nicht geladen werden:", err);
  }

  render() {
    if (!this.state.err) return this.props.children;
    // Ein gescheiterter Chunk ist der mit Abstand haeufigste Fall — deshalb
    // steht das Neuladen vorn und nicht die technische Meldung.
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, height: "100dvh", padding: 24, textAlign: "center" }}>
        <Icon name="warning-circle" size={34} style={{ color: "var(--text-mute)" }} />
        <div>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "var(--text)" }}>Diese Ansicht konnte nicht geladen werden</p>
          <p style={{ margin: "6px 0 0", fontSize: 13.5, color: "var(--text-mute)", maxWidth: 380 }}>
            Meist liegt es an einer veralteten Fassung im Browserspeicher. Ein Neuladen holt die aktuelle.
          </p>
        </div>
        <Btn
          onClick={() => {
            // Riegel loesen, damit das Neuladen wirklich einen neuen Versuch bekommt
            chunkRiegelLoesen();
            window.location.reload();
          }}
        >
          Neu laden
        </Btn>
        <code style={{ fontSize: 11, color: "var(--text-mute)", opacity: 0.75, maxWidth: 460, wordBreak: "break-word" }}>{String(this.state.err.message ?? this.state.err).slice(0, 200)}</code>
      </div>
    );
  }
}

// Heavy screens are code-split so the landing page doesn't ship maplibre-gl (~1MB,
// only /karte) or echarts (~1MB, only the project charts) or the admin bundle.
// This was the real cause of the slow first paint on mobile (a 3.3MB monolith).
const ProjectStats = lazy(() => retryOnStaleShell(import("./screens/dashboard/ProjectStats").then((m) => ({ default: m.ProjectStats }))));
const KarteScreen = lazy(() => retryOnStaleShell(import("./screens/karte/KarteScreen").then((m) => ({ default: m.KarteScreen }))));
const AdminScreen = lazy(() => retryOnStaleShell(import("./screens/admin/AdminScreen").then((m) => ({ default: m.AdminScreen }))));
const UebersichtScreen = lazy(() => retryOnStaleShell(import("./screens/karte/UebersichtScreen").then((m) => ({ default: m.UebersichtScreen }))));
const AccountScreen = lazy(() => retryOnStaleShell(import("./screens/AccountScreen").then((m) => ({ default: m.AccountScreen }))));
// Oeffentliche Meldeseite (Buergermeldungen) — eigener Chunk, Besucher laden nichts von der Anwendung mit
const MeldenScreen = lazy(() => retryOnStaleShell(import("./screens/MeldenScreen").then((m) => ({ default: m.MeldenScreen }))));
// Meldeportal in der Anwendung — Meldungen je Kommune selbst erfassen
const MeldeportalScreen = lazy(() => retryOnStaleShell(import("./screens/MeldeportalScreen").then((m) => ({ default: m.MeldeportalScreen }))));
// Funktionswünsche — einreichen und abstimmen (Fußzeile)
const WuenscheScreen = lazy(() => retryOnStaleShell(import("./screens/WuenscheScreen").then((m) => ({ default: m.WuenscheScreen }))));
const LegalScreen = lazy(() => retryOnStaleShell(import("./screens/LegalScreen").then((m) => ({ default: m.LegalScreen }))));

// SeitenSkelett: gemeinsames Drahtgitter (auch ProjectStats und die Shell
// nutzen es) — s. components/SeitenSkelett.
import { SeitenSkelett } from "./components/SeitenSkelett";
import { api, setUnauthorizedHandler } from "./api/http";
import { useRealtimeSync } from "./api/realtime";
import { POLL_MS, neueFassungVerfuegbar } from "./lib/version";
import { useStore } from "./store/useStore";

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false } },
});

/**
 * Sieht nach, ob der Server eine andere Fassung ausliefert — beim Start, bei
 * jedem Zurueckkehren ins Fenster und alle zehn Minuten.
 *
 * Der Zeitpunkt „Fenster wieder im Vordergrund" ist der wichtigste: genau dann
 * hat jemand einen Laptop aufgeklappt, der ueber ein Deployment hinweg zu war.
 * Ein Aufruf kostet ein paar hundert Byte, und sobald etwas gefunden ist,
 * hoert das Nachsehen auf.
 */
function useVersionWatch(): void {
  const setUpdateReady = useStore((s) => s.setUpdateReady);
  const updateReady = useStore((s) => s.updateReady);
  useEffect(() => {
    if (updateReady) return; // gefunden ist gefunden
    let weg = false;
    const sehen = () => {
      void neueFassungVerfuegbar().then((neu) => {
        if (neu && !weg) setUpdateReady(true);
      });
    };
    // beim Start NICHT sofort: der erste Bildschirm soll die Leitung fuer sich
    // haben. Zehn Sekunden spaeter ist alles Wichtige laengst unterwegs.
    const start = window.setTimeout(sehen, 10_000);
    const timer = window.setInterval(sehen, POLL_MS);
    const beiSichtbar = () => {
      if (document.visibilityState === "visible") sehen();
    };
    document.addEventListener("visibilitychange", beiSichtbar);
    window.addEventListener("online", sehen);
    return () => {
      weg = true;
      window.clearTimeout(start);
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", beiSichtbar);
      window.removeEventListener("online", sehen);
    };
  }, [updateReady, setUpdateReady]);
}

/**
 * Laedt beim naechsten Seitenwechsel von selbst neu — aber nur, wenn nichts
 * offen ist.
 *
 * Der Seitenwechsel ist der richtige Moment: genau dann wuerde die alte Huelle
 * einen Chunk anfordern, den es nicht mehr gibt. Wer dagegen auf EINEM
 * Bildschirm bleibt, wird nicht unterbrochen — fuer den steht das Band unten.
 *
 * `dirtyKeys` ist die Handbremse: ein offener Entwurf im Feld ueberlebt kein
 * Neuladen, eine veraltete Fassung dagegen ist bloss unbequem.
 */
function useReloadOnNavigate(): void {
  const updateReady = useStore((s) => s.updateReady);
  const dirty = useStore((s) => s.dirtyKeys.length > 0);
  const { pathname } = useLocation();
  const vorher = useRef(pathname);
  useEffect(() => {
    const gewechselt = vorher.current !== pathname;
    vorher.current = pathname;
    // Nur beim TATSAECHLICHEN Wechsel, nicht wenn spaeter bloss der Zustand
    // umspringt — sonst laedt die Seite mitten in der Arbeit neu, nur weil
    // gerade ein Entwurf gespeichert wurde.
    if (gewechselt && updateReady && !dirty) window.location.reload();
  }, [pathname, updateReady, dirty]);
}

/**
 * Hinweisband fuer alle, die auf einem Bildschirm bleiben.
 *
 * Unten links, ueber allem, aber schmal — es unterbricht nichts und laesst sich
 * wegschieben. Kein Modal: die Fassung ist eine Empfehlung, keine Bedingung.
 */
function UpdateBanner() {
  const updateReady = useStore((s) => s.updateReady);
  const dirty = useStore((s) => s.dirtyKeys.length > 0);
  const [weg, setWeg] = useState(false);
  if (!updateReady || weg) return null;
  return (
    <div
      className="glass-strong anim-pop"
      role="status"
      style={{ position: "fixed", left: 16, bottom: "calc(16px + var(--safe-bottom))", zIndex: 3500, display: "flex", alignItems: "center", gap: 12, padding: "10px 12px 10px 14px", borderRadius: 14, boxShadow: "var(--shadow-lg)", maxWidth: "min(420px, calc(100vw - 32px))" }}
    >
      <Icon name="arrows-clockwise" size={16} style={{ color: "var(--accent-text)", flex: "none" }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>Neue Fassung verfügbar</div>
        <div style={{ fontSize: 11.5, color: "var(--text-mute)" }}>
          {dirty ? "Erst speichern — danach neu laden." : "Ein Neuladen holt sie."}
        </div>
      </div>
      <Btn size="sm" onClick={() => window.location.reload()}>
        Neu laden
      </Btn>
      <IconBtn icon="x" size={14} box={28} aria-label="Ausblenden" onClick={() => setWeg(true)} />
    </div>
  );
}

/** Traegt die Haken, die den Router-Kontext brauchen. Rendert nichts. */
function NavigationEffects() {
  useReloadOnNavigate();
  return null;
}

export function App() {
  const setAuth = useStore((s) => s.setAuth);
  // reconcile cached auth with the server session on boot
  useEffect(() => {
    // DAS hier ist das berechtigte automatische Abmelden: nicht bei einer
    // veralteten Fassung, sondern wenn die SITZUNG wirklich weg ist. Ein
    // Fenster, das ueber Nacht offen bleibt, zeigte danach eine Oberflaeche,
    // in der jeder Klick mit einem Fehler endete. Jetzt landet es auf der
    // Anmeldung (RequireAuth greift, sobald auth null ist).
    setUnauthorizedHandler(() => setAuth(null));
    api
      .me()
      .then((u) => setAuth(u))
      .catch(() => undefined)
      .finally(() => useStore.getState().setAuthGeprueft());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <AppRoutes />
    </QueryClientProvider>
  );
}

function AppRoutes() {
  useRealtimeSync(); // SSE → live invalidation of form/field/layer/feature queries
  useVersionWatch();
  return (
    <BrowserRouter>
      <ScreenBoundary>
      <NavigationEffects />
      <KommunenAkzent />
      <UpdateBanner />
      {/* Seit Shell/MapShell ein eigenes Suspense um den Outlet legen, greift
          dieses hier nur noch fuer die Routen AUSSERHALB der Shell (Login,
          Reset). Die tragen kein Dashboard — ein neutraler Rahmen genuegt. */}
      <Suspense fallback={<div style={{ minHeight: "50vh" }} aria-busy />}>
      <Routes>
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/demo" element={<DemoEinstieg ziel="demo" />} />
        {/* Alt-Link: demo2 war die Insel, bevor sie die Hauptdemo wurde */}
        <Route path="/demo2" element={<DemoEinstieg ziel="demo" />} />
        <Route path="/reset" element={<ResetScreen />} />
        <Route path="/melden/:kennung" element={<MeldenScreen />} />

        <Route element={<Shell />}>
          {/* Eigener Hostname fuer die Demo: demo.webgis.lilasp.de fuehrt ohne
              Pfad direkt in die Lese-Demo. Die Entscheidung faellt am Hostnamen
              und nicht per Umleitung im Proxy, weil der Einstieg eine Anmeldung
              braucht (DemoEinstieg holt sie) — ein reiner 302 auf /demo haette
              denselben Weg nur laenger gemacht. */}
          <Route path="/" element={location.hostname.startsWith("demo.") ? <DemoEinstieg ziel="demo" /> : <HomeRedirect />} />
          <Route
            path="/overview"
            element={
              <RequireAuth>
                <ProjectOverview />
              </RequireAuth>
            }
          />
          <Route
            path="/project/:projectId"
            element={
              <RequireAuth>
                <ProjectStats />
              </RequireAuth>
            }
          />
          <Route
            path="/admin"
            element={
              <RequireVerwaltung>
                <AdminScreen />
              </RequireVerwaltung>
            }
          />
          <Route
            path="/account"
            element={
              <RequireAuth>
                <AccountScreen />
              </RequireAuth>
            }
          />
          <Route
            path="/meldeportal"
            element={
              <RequireAuth>
                <MeldeportalScreen />
              </RequireAuth>
            }
          />
          <Route
            path="/meldeportal/:projectId"
            element={
              <RequireAuth>
                <MeldeportalScreen />
              </RequireAuth>
            }
          />
          <Route
            path="/wuensche"
            element={
              <RequireAuth>
                <WuenscheScreen />
              </RequireAuth>
            }
          />
          <Route path="/legal/:page" element={<LegalScreen />} />
          <Route path="*" element={<Fallback name="404" />} />
        </Route>

        <Route element={<MapShell />}>
          <Route
            path="/karte"
            element={
              <RequireAuth>
                <KarteScreen />
              </RequireAuth>
            }
          />
          <Route
            path="/uebersicht"
            element={
              <RequireAdmin>
                <UebersichtScreen />
              </RequireAdmin>
            }
          />
        </Route>
      </Routes>
      </Suspense>
      </ScreenBoundary>
    </BrowserRouter>
  );
}

/**
 * Oeffentlicher Demo-Einstieg (Lese-Demo Playground Island): meldet still als
 * Demo-Konto an und springt in das Kunstprojekt. Jede mutierende Route weist
 * der Server fuer diese Sitzung ab; Aenderungen leben nur im Browser.
 */
function DemoEinstieg({ ziel }: { ziel: string }) {
  const setAuth = useStore((s) => s.setAuth);
  const navigate = useNavigate();
  const [fehler, setFehler] = useState(false);
  useEffect(() => {
    let weg = false;
    api
      .demoLogin()
      .then((u) => {
        if (weg) return;
        setAuth(u);
        navigate(`/karte?project=${ziel}`, { replace: true });
      })
      .catch(() => !weg && setFehler(true));
    return () => {
      weg = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-mute)", fontSize: 14 }}>
      {fehler ? "Demo derzeit nicht verfügbar." : "Demo wird gestartet…"}
    </div>
  );
}
